import { forwardRef, useImperativeHandle } from "react"
import { Box, VStack, Text, Skeleton, HStack } from "@chakra-ui/react"
import { Alert } from "@hydrologiq/shared/chakra/Alert"
import { AssetAvailabilityCalendarSimple, getPeriodStart } from "./AssetAvailabilityCalendar"
import { ActivityKind } from "@hydrologiq/gql/graphql"
import { format as formatDate, isValid, parseISO } from "date-fns"
import OrdersDrawerContent from "./OrdersDrawerOverlay"
import { useCalendarData } from "./useCalendarData"

interface AssetAvailabilityCalendarIsolatedProps {
  onBack?: () => void
  onActivityEdit?: (activityId: string, assetId: string) => void
}

export interface AssetAvailabilityCalendarHandle {
  handleBackClick: () => void
  /** Force a reload of calendar + orders data (e.g. after an activity edit/delete elsewhere). */
  refresh: () => void
}

/**
 * Calendar component that owns its data fetching and reads directly from context.
 * This eliminates prop-passing chains that cause unnecessary re-renders.
 */
const AssetAvailabilityCalendarIsolatedInner = forwardRef<
  AssetAvailabilityCalendarHandle,
  AssetAvailabilityCalendarIsolatedProps
>(({ onBack, onActivityEdit }, ref) => {
  // Use the extracted hook for all data fetching and state management
  const {
    displayData,
    ordersData,
    isInitialLoad,
    isRefetching,
    ordersLoading,
    error,
    ordersError,
    anchorDate,
    endDate,
    granularity,
    loadedDateRange,
    isDrawerOpen,
    filteredOrder,
    highlightedAssetIds,
    handleAnchorDateChange,
    handleCalendarBufferChange,
    handleLoadMore,
    handleRefresh,
    handleVisiblePeriodChange,
    handleFilterOrder,
    handleClearFilter,
    setIsDrawerOpen,
    setGranularity,
    refetchOrders,
    refetch,
  } = useCalendarData()

  // Imperative refresh for parents that render the activity dialog alongside the calendar.
  useImperativeHandle(
    ref,
    () => ({
      handleBackClick: () => onBack?.(),
      refresh: () => {
        handleRefresh()
        refetchOrders()
      },
    }),
    [onBack, handleRefresh, refetchOrders],
  )

  const displayStartDate = anchorDate
  const displayEndDate = endDate

  // Skeleton loader for initial load — matches the calendar layout structure
  const CalendarSkeleton = () => (
    <VStack gap={3} width="100%" height="100%" align="stretch" p={4}>
      {/* Toolbar skeleton */}
      <HStack gap={3} width="100%">
        <Skeleton height="36px" width="70px" borderRadius="md" />
        <Skeleton height="36px" width="36px" borderRadius="md" />
        <Skeleton height="36px" width="36px" borderRadius="md" />
        <Skeleton height="36px" width="140px" borderRadius="md" />
        <Skeleton height="36px" width="100px" borderRadius="md" />
        <Box flex={1} />
        <Skeleton height="36px" width="180px" borderRadius="md" />
      </HStack>

      {/* Activity filter pills skeleton */}
      <HStack gap={2} width="100%">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} height="30px" width={`${80 + i * 10}px`} borderRadius="full" />
        ))}
        <Box flex={1} />
        <Skeleton height="36px" width="300px" borderRadius="md" />
      </HStack>

      {/* Timeline skeleton — header + rows */}
      <Box borderWidth="1px" borderColor="border" borderRadius="md" flex={1} overflow="hidden">
        {/* Header row */}
        <HStack bg="bg.subtle" p={3} borderBottom="1px solid" borderColor="border">
          <Skeleton height="20px" width="60px" />
          <Box flex={1} />
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} height="16px" width="28px" />
          ))}
        </HStack>
        {/* Site/asset rows */}
        <VStack gap={0} align="stretch">
          {Array.from({ length: 6 }).map((_, i) => (
            <HStack key={i} p={3} borderBottom="1px solid" borderColor="border.muted" height="60px">
              <Skeleton height="16px" width={i % 3 === 0 ? "180px" : "140px"} />
              <Box flex={1} display="flex" gap={1} pl={4}>
                {i % 3 !== 2 && <Skeleton height="40px" width={`${30 + (i * 7) % 40}%`} borderRadius="md" />}
              </Box>
            </HStack>
          ))}
        </VStack>
      </Box>
    </VStack>
  )

  // Validation checks - after all hooks are called
  // Don't render if we don't have valid dates
  if (!displayStartDate || !displayEndDate) {
    return (
      <VStack height="100%" justify="center" align="center" p={8}>
        <Text color="gray.500" fontSize="lg">
          Please select start and end dates in the order form first.
        </Text>
      </VStack>
    )
  }

  // Show skeleton only on very first load
  if (isInitialLoad && !displayData) {
    return (
      <Box width="100%" height="100%">
        <CalendarSkeleton />
      </Box>
    )
  }

  // Show error only if no data available
  if (error && !displayData) {
    return (
      <Box width="100%" height="100%">
        <Alert status="error" title="Error loading calendar">
          {error.message}
        </Alert>
      </Box>
    )
  }

  // Show empty state
  if (!isRefetching && !isInitialLoad && !error && displayData?.data?.length === 0) {
    return (
      <Box width="100%" height="100%">
        <VStack height="100%" justify="center" align="center">
          <Text color="gray.500">No assets found for the selected date range.</Text>
        </VStack>
      </Box>
    )
  }

  // Once we have data, ALWAYS render the calendar (even during refetch)
  // This prevents unmounting/remounting which causes flickering
  if (displayData) {
    return (
      <>
        <Box width="100%" height="100%" position="relative">
          <Box width="100%" height="100%">
            <AssetAvailabilityCalendarSimple
              data={displayData as any}
              startDate={displayStartDate}
              endDate={displayEndDate}
              isRefreshing={isRefetching}
              onAnchorDateChange={handleAnchorDateChange}
              onCalendarBufferChange={handleCalendarBufferChange}
              disableStatusFilter={true}
              booking={false}
              highlightedAssetIds={highlightedAssetIds}
              filteredOrderName={filteredOrder?.name}
              filteredOrderId={filteredOrder?.id}
              onClearFilter={handleClearFilter}
              highlightedOrderDates={
                filteredOrder && filteredOrder.start
                  ? {
                    start: filteredOrder.start,
                    end: filteredOrder.end ?? loadedDateRange?.end ?? endDate,
                  }
                  : undefined
              }
              onOrdersToggle={() => setIsDrawerOpen(!isDrawerOpen)}
              onRefresh={handleRefresh}
              onLoadMore={handleLoadMore}
              granularity={granularity}
              loadedDateRange={loadedDateRange || undefined}
              onVisiblePeriodChange={handleVisiblePeriodChange}
              orders={ordersData?.orders?.data || []}
              onGranularityChange={(newGranularity) => {
                setGranularity(newGranularity)
                // Update anchor date to period start when granularity changes
                const anchor = parseISO(anchorDate)
                if (isValid(anchor)) {
                  const newPeriodStart = getPeriodStart(anchor, newGranularity)
                  const newPeriodStartStr = formatDate(newPeriodStart, "yyyy-MM-dd")
                  // Trigger anchor date change which will handle refetch if needed
                  handleAnchorDateChange(newPeriodStartStr)
                }
              }}
              onActivityEdit={(activityId: string, assetId: string) => {
                onActivityEdit?.(activityId, assetId)
                const activity = displayData?.data.find((activity: any) => activity.id === activityId)
                if (activity && activity.kind === ActivityKind.Fulfilment) {
                  refetchOrders()
                }
              }}
              onActivityCreated={() => {
                refetch()
                refetchOrders()
              }}
            />
          </Box>
        </Box>

        {/* Orders drawer viewport */}
        {/* <ordersDrawer.Viewport /> */}
        <OrdersDrawerContent
          orders={ordersData?.orders?.data || []}
          loading={ordersLoading}
          error={ordersError?.message || null}
          onFilterOrder={handleFilterOrder}
          hasMore={false}
          open={isDrawerOpen}
          onOpenChange={(details) => setIsDrawerOpen(details.open)}
        />
      </>
    )
  }

  // Fallback (should never reach here)
  return null
})

AssetAvailabilityCalendarIsolatedInner.displayName = "AssetAvailabilityCalendarIsolated"

export default AssetAvailabilityCalendarIsolatedInner
